import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCw, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { ShareWinCard } from "../components/ShareWinCard";
import { ReferralNudge } from "../components/ReferralNudge";
import { PastPaper } from "../components/PastPaper";

const tabBtn = (active) =>
  `flex-1 py-2.5 border-2 border-black rounded-full font-bold text-sm transition-all ${
    active ? "bg-[#4D96FF] shadow-[3px_3px_0px_rgba(0,0,0,1)]" : "bg-white text-gray-500"
  }`;

function Notes({ notes }) {
  return (
    <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6" data-testid="study-notes">
      {notes.split("\n").map((line, i) => {
        if (line.startsWith("## ")) return <h2 key={i} className="font-heading text-lg font-semibold mt-4 mb-1">{line.slice(3)}</h2>;
        if (line.startsWith("# ")) return <h2 key={i} className="font-heading text-xl font-semibold mt-4 mb-1">{line.slice(2)}</h2>;
        const parts = line.split("**");
        const rendered = parts.map((p, j) => (j % 2 ? <b key={j}>{p}</b> : p));
        if (line.startsWith("- ")) return <p key={i} className="text-sm leading-relaxed pl-4 relative before:content-['•'] before:absolute before:left-0">{rendered.map((r, j) => j === 0 && typeof r === "string" ? r.slice(2) : r)}</p>;
        return <p key={i} className="text-sm leading-relaxed min-h-[8px]">{rendered}</p>;
      })}
    </div>
  );
}

function Flashcards({ cards }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const card = cards[idx];
  return (
    <div className="space-y-4">
      <button
        onClick={() => setFlipped(!flipped)}
        data-testid="flashcard"
        className={`w-full min-h-[220px] border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 flex flex-col items-center justify-center text-center transition-colors ${
          flipped ? "bg-[#F2C6DE]" : "bg-[#FFE066]"
        }`}
      >
        <p className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2">{flipped ? "Answer" : "Tap to flip"}</p>
        <p className={`font-medium ${flipped ? "text-sm" : "font-heading text-xl"}`}>{flipped ? card.back : card.front}</p>
      </button>
      <div className="flex items-center justify-between">
        <button onClick={() => { setIdx((idx - 1 + cards.length) % cards.length); setFlipped(false); }} data-testid="flashcard-prev" className="p-3 bg-white border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all">
          <ChevronLeft size={18} />
        </button>
        <p className="font-heading font-semibold" data-testid="flashcard-progress">{idx + 1} / {cards.length}</p>
        <button onClick={() => { setIdx((idx + 1) % cards.length); setFlipped(false); }} data-testid="flashcard-next" className="p-3 bg-white border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all">
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

function Quiz({ questions, title }) {
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const q = questions[idx];

  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === q.answer) setScore((s) => s + 1);
  };
  const next = () => {
    if (idx + 1 >= questions.length) setDone(true);
    else { setIdx(idx + 1); setPicked(null); }
  };
  const restart = () => { setIdx(0); setPicked(null); setScore(0); setDone(false); };

  if (done) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-8 text-center space-y-3" data-testid="quiz-result">
        <p className="font-heading text-4xl font-semibold">{score} / {questions.length}</p>
        <p className="text-sm text-gray-700">
          {pct >= 80 ? "That's A-grade territory. Seriously impressive." : pct >= 50 ? "Solid base! Hit the flashcards on the ones you missed and re-quiz." : "First pass done — that's the hard part. Flip through the notes and try again."}
        </p>
        <button onClick={restart} data-testid="quiz-restart-button" className="bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold px-6 py-2.5 inline-flex items-center gap-2">
          <RotateCw size={16} /> Try again
        </button>
        <div>
          <ShareWinCard
            stat={`${score}/${questions.length}`}
            title={pct >= 80 ? "A-grade territory!" : "Quiz smashed!"}
            sub={title ? `Topic: ${title}` : "ATAR-aligned quiz with Mia"}
            color={pct >= 80 ? "#FFE066" : "#4D96FF"}
            testId="share-quiz-button"
          />
        </div>
        {pct >= 80 && <div className="max-w-xs mx-auto"><ReferralNudge /></div>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs font-bold uppercase tracking-widest text-gray-600">Question {idx + 1} of {questions.length} · Score {score}</p>
      <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5">
        <p className="font-bold mb-4" data-testid="quiz-question">{q.question}</p>
        <div className="space-y-2">
          {q.options.map((opt, i) => {
            let cls = "bg-white";
            if (picked !== null) {
              if (i === q.answer) cls = "bg-[#51CF66]";
              else if (i === picked) cls = "bg-[#FF6B6B] text-white";
              else cls = "bg-white opacity-60";
            }
            return (
              <button
                key={`${idx}-${opt}`}
                onClick={() => pick(i)}
                data-testid={`quiz-option-${i}`}
                className={`w-full flex items-center gap-2 text-left text-sm font-medium p-3 border-2 border-black rounded-xl shadow-[2px_2px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all ${cls}`}
              >
                {picked !== null && i === q.answer && <CheckCircle2 size={16} className="shrink-0" />}
                {picked !== null && i === picked && i !== q.answer && <XCircle size={16} className="shrink-0" />}
                {opt}
              </button>
            );
          })}
        </div>
        {picked !== null && (
          <div className="mt-4 p-3 bg-[#FFE066]/50 border-2 border-black rounded-xl text-sm" data-testid="quiz-explanation">
            <span className="font-bold">Mia:</span> {q.explanation}
          </div>
        )}
      </div>
      {picked !== null && (
        <button onClick={next} data-testid="quiz-next-button" className="w-full bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3">
          {idx + 1 >= questions.length ? "See my score" : "Next question"}
        </button>
      )}
    </div>
  );
}

export default function StudySetPage() {
  const { id } = useParams();
  const [set, setSet] = useState(null);
  const [tab, setTab] = useState("notes");

  useEffect(() => {
    api.get(`/study-sets/${id}`).then((r) => setSet(r.data)).catch(() => {});
  }, [id]);

  if (!set) {
    return <div className="max-w-lg mx-auto px-5 pt-16 flex justify-center"><Loader2 size={28} className="animate-spin" /></div>;
  }

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/study" data-testid="back-to-study" className="p-2.5 bg-white border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all">
          <ArrowLeft size={18} />
        </Link>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold leading-tight truncate">{set.title}</h1>
          <p className="text-sm text-gray-600">{set.subject} · ATAR-aligned</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab("notes")} data-testid="tab-notes" className={tabBtn(tab === "notes")}>Notes</button>
        <button onClick={() => setTab("cards")} data-testid="tab-flashcards" className={tabBtn(tab === "cards")}>Cards</button>
        <button onClick={() => setTab("quiz")} data-testid="tab-quiz" className={tabBtn(tab === "quiz")}>Quiz</button>
        <button onClick={() => setTab("exam")} data-testid="tab-exam" className={tabBtn(tab === "exam")}>Exam</button>
      </div>

      {tab === "notes" && <Notes notes={set.notes} />}
      {tab === "cards" && (set.flashcards.length ? <Flashcards cards={set.flashcards} /> : <p className="text-sm text-gray-600">No flashcards.</p>)}
      {tab === "quiz" && (set.quiz.length ? <Quiz questions={set.quiz} title={set.title} /> : <p className="text-sm text-gray-600">No quiz.</p>)}
      {tab === "exam" && <PastPaper setId={id} title={set.title} />}
    </div>
  );
}
